// Utility functions for managing assessment progress in localStorage

import {
  AssessmentProgress,
  STORAGE_KEYS,
  CURRENT_VERSION,
  EXPIRATION_DAYS,
} from '../types/assessment-progress';
// UUID generation is handled by generateUUID function below

/**
 * Generate a new UUID
 * Since we don't have uuid package, we'll create a simple implementation
 */
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Calculate expiration date (7 days from now)
 */
const getExpirationDate = (): string => {
  const date = new Date();
  date.setDate(date.getDate() + EXPIRATION_DAYS);
  return date.toISOString();
};

/**
 * Create empty progress structure
 */
export const createEmptyProgress = (userId: string | null = null): AssessmentProgress => {
  return {
    version: CURRENT_VERSION,
    sessionId: generateUUID(),
    userId,
    lastSaved: new Date().toISOString(),
    currentStep: 'info',
    expiresAt: getExpirationDate(),
    data: {
      step1: {
        companyName: '',
        industry: null,
        role: null,
        completedAt: null,
      },
      step2: {
        role: null,
        completedAt: null,
      },
      step3: {
        questionSet: null,
        aiQuestions: [],
        aiAnswers: {},
        reviewedAt: null,
      },
      step4: {
        additionalQuestions: [],
        additionalAnswers: {},
        questionRegenerations: {},
        skippedQuestions: [],
        progress: {
          answered: 0,
          total: 0,
        },
        completedAt: null,
      },
      step5: {
        documents: [],
        lastUploadedAt: null,
      },
    },
  };
};

/**
 * Check if progress is expired
 */
export const isProgressExpired = (progress: AssessmentProgress): boolean => {
  try {
    const expirationDate = new Date(progress.expiresAt);
    const now = new Date();
    return expirationDate < now;
  } catch (error) {
    console.error('Error checking expiration:', error);
    return true; // Treat as expired if we can't parse date
  }
};

/**
 * Validate progress structure
 */
export const isValidProgress = (progress: any): progress is AssessmentProgress => {
  try {
    if (!progress || typeof progress !== 'object') return false;
    if (!progress.version || !progress.sessionId) return false;
    if (!progress.data || typeof progress.data !== 'object') return false;
    if (
      !progress.data.step1 ||
      !progress.data.step2 ||
      !progress.data.step3 ||
      !progress.data.step4 ||
      !progress.data.step5
    ) {
      return false;
    }
    return true;
  } catch (error) {
    console.error('Error validating progress:', error);
    return false;
  }
};

/**
 * Check if progress has meaningful data (not just empty structure)
 */
export const hasMeaningfulData = (progress: AssessmentProgress): boolean => {
  return progress.data.step1.industry !== null;
};

/**
 * Save progress to localStorage
 */
export const saveProgress = (progress: AssessmentProgress): boolean => {
  try {
    // Update timestamps
    progress.lastSaved = new Date().toISOString();
    progress.expiresAt = getExpirationDate();

    // Don't save if no meaningful data
    if (!hasMeaningfulData(progress)) {
      return false;
    }

    const serialized = JSON.stringify(progress);
    localStorage.setItem(STORAGE_KEYS.PROGRESS, serialized);
    
    // Also save version separately for quick checks
    localStorage.setItem(STORAGE_KEYS.VERSION, CURRENT_VERSION);
    
    return true;
  } catch (error) {
    console.error('Error saving progress to localStorage:', error);
    
    // Handle QuotaExceededError
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.error('localStorage quota exceeded. Consider clearing old data.');
    }
    
    return false;
  }
};

/**
 * Load progress from localStorage
 */
export const loadProgress = (): AssessmentProgress | null => {
  try {
    const serialized = localStorage.getItem(STORAGE_KEYS.PROGRESS);
    
    if (!serialized) {
      return null;
    }

    const progress = JSON.parse(serialized) as AssessmentProgress;

    // Validate structure
    if (!isValidProgress(progress)) {
      console.warn('Invalid progress structure found, clearing...');
      clearProgress();
      return null;
    }

    // Check version compatibility
    if (progress.version !== CURRENT_VERSION) {
      console.warn(`Version mismatch: ${progress.version} !== ${CURRENT_VERSION}`);
      // In future, implement migration logic here
      clearProgress();
      return null;
    }

    // Check expiration
    if (isProgressExpired(progress)) {
      console.info('Progress expired, clearing...');
      clearProgress();
      return null;
    }

    return progress;
  } catch (error) {
    console.error('Error loading progress from localStorage:', error);
    clearProgress();
    return null;
  }
};

/**
 * Clear all progress data
 */
export const clearProgress = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.PROGRESS);
    localStorage.removeItem(STORAGE_KEYS.VERSION);
    localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
    
    // Also remove old assessment data if exists
    localStorage.removeItem('currentAssessment');
  } catch (error) {
    console.error('Error clearing progress:', error);
  }
};

/**
 * Get user ID from Clerk or localStorage
 */
export const getUserId = (): string | null => {
  try {
    // Try to get from auth token or Clerk context
    // For now, return null and let the component provide it
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Format relative time (e.g., "2 hours ago")
 */
export const getRelativeTime = (date: Date | string): string => {
  try {
    const now = new Date();
    const then = typeof date === 'string' ? new Date(date) : date;
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return then.toLocaleDateString();
  } catch (error) {
    return 'Unknown';
  }
};

/**
 * Get step display name
 */
export const getStepDisplayName = (step: AssessmentProgress['currentStep']): string => {
  const stepNames: Record<AssessmentProgress['currentStep'], string> = {
    info: 'Company & Industry Info',
    overview: 'Review & AI Questions',
    'additional-questions': 'Personalized Questions',
    'document-upload': 'Document Upload',
  };
  return stepNames[step] || step;
};

/**
 * Listen to storage changes (for multi-tab sync)
 */
export const onStorageChange = (callback: (progress: AssessmentProgress | null) => void): (() => void) => {
  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === STORAGE_KEYS.PROGRESS) {
      if (e.newValue) {
        try {
          const progress = JSON.parse(e.newValue) as AssessmentProgress;
          callback(progress);
        } catch (error) {
          console.error('Error parsing storage change:', error);
          callback(null);
        }
      } else {
        callback(null);
      }
    }
  };

  window.addEventListener('storage', handleStorageChange);

  // Return cleanup function
  return () => {
    window.removeEventListener('storage', handleStorageChange);
  };
};

